import React, { useMemo, useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, X, Loader2, Globe } from 'lucide-react';
import { addAvailabilityBlock, deleteAvailabilityBlock, updateTrainerFullProfile } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Phoenix', label: 'Arizona — no DST (MST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
];

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

/**
 * Read a day's blocks whether stored as new-shape ({blocks:[...]}) or
 * legacy shape ({start,end}).
 */
const readBlocks = (day) => {
  if (!day) return [];
  if (Array.isArray(day.blocks) && day.blocks.length) {
    return day.blocks
      .filter((b) => b && b.start && b.end)
      .map((b) => ({ start: b.start, end: b.end }));
  }
  if (day.start && day.end) return [{ start: day.start, end: day.end }];
  return [];
};

const parseHHMM = (s) => {
  const [h, m] = String(s).split(':').map((x) => Number(x));
  return h * 60 + m;
};

const WeeklyScheduleEditor = ({ value, onChange }) => {
  // `value` is the schedule map. We manage it in a local copy and call
  // onChange after every add/remove so the parent can flush to storage
  // and its own useEffect picks up the latest snapshot.
  const schedule = value || {};
  const { user, refreshUser } = useAuth();

  const [tz, setTz] = useState(user?.trainer_profile?.timezone || 'America/New_York');
  const [tzSaving, setTzSaving] = useState(false);

  const handleTzChange = async (next) => {
    const prev = tz;
    setTz(next);
    setTzSaving(true);
    try {
      await updateTrainerFullProfile({ trainer_profile: { timezone: next } });
      await refreshUser?.();
      toast.success('Timezone saved');
    } catch (err) {
      setTz(prev);
      toast.error(err?.response?.data?.detail || 'Could not save timezone');
    } finally {
      setTzSaving(false);
    }
  };

  const [busyKey, setBusyKey] = useState('');
  // Per-day draft state for the "Add block" row
  const [draft, setDraft] = useState(() => {
    const seed = {};
    DAYS.forEach((d) => { seed[d.key] = { start: '09:00', end: '17:00' }; });
    return seed;
  });

  const setDraftFor = (key, patch) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const commit = (nextSchedule) => {
    onChange?.(nextSchedule);
  };

  const handleAdd = async (dayKey) => {
    const { start, end } = draft[dayKey] || {};
    if (!start || !end) {
      toast.error('Start and end are required');
      return;
    }
    if (parseHHMM(start) >= parseHHMM(end)) {
      toast.error('Start must be before end');
      return;
    }
    setBusyKey(`add-${dayKey}`);
    try {
      const res = await addAvailabilityBlock(dayKey, start, end);
      const next = {
        ...schedule,
        [dayKey]: { enabled: true, blocks: res.data.blocks },
      };
      commit(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add block');
    } finally {
      setBusyKey('');
    }
  };

  const handleRemove = async (dayKey, index) => {
    setBusyKey(`del-${dayKey}-${index}`);
    try {
      const res = await deleteAvailabilityBlock(dayKey, index);
      const next = {
        ...schedule,
        [dayKey]: { enabled: !!res.data.enabled, blocks: res.data.blocks || [] },
      };
      commit(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove block');
    } finally {
      setBusyKey('');
    }
  };

  const dayEntries = useMemo(
    () => DAYS.map((d) => ({ ...d, blocks: readBlocks(schedule[d.key]) })),
    [schedule]
  );

  return (
    <div className="space-y-3" data-testid="weekly-schedule-editor">
      <div
        className="rounded-md border border-zinc-800 bg-[#09090B] p-3 flex items-center gap-3"
        data-testid="schedule-timezone-row"
      >
        <Globe className="w-4 h-4 text-[#C0A062] shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">Your timezone</p>
          <p className="text-[11px] text-zinc-500">All times below are in this timezone.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {tzSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />}
          <Select value={tz} onValueChange={handleTzChange} disabled={tzSaving}>
            <SelectTrigger
              className="bg-[#18181B] border-zinc-700 text-white w-48"
              data-testid="schedule-timezone-select"
            >
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {US_TIMEZONES.map((z) => (
                <SelectItem key={z.value} value={z.value} data-testid={`tz-option-${z.value}`}>
                  {z.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {dayEntries.map(({ key, label, blocks }) => {
        const addBusy = busyKey === `add-${key}`;
        return (
          <div
            key={key}
            className="rounded-md border border-zinc-800 bg-[#09090B] p-3"
            data-testid={`schedule-row-${key}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-white">{label}</span>
              <span className="text-[11px] text-zinc-500">
                {blocks.length} block{blocks.length === 1 ? '' : 's'}
              </span>
            </div>

            {blocks.length === 0 ? (
              <p className="text-xs text-zinc-500 mb-2">Unavailable this day.</p>
            ) : (
              <ul className="space-y-2 mb-3">
                {blocks.map((b, i) => {
                  const delBusy = busyKey === `del-${key}-${i}`;
                  return (
                    <li
                      key={`${key}-${i}-${b.start}-${b.end}`}
                      className="flex items-center gap-2 text-sm text-zinc-200 bg-[#18181B] border border-zinc-800 rounded px-3 py-1.5"
                      data-testid={`schedule-block-${key}-${i}`}
                    >
                      <span className="font-mono">{b.start}</span>
                      <span className="text-zinc-500 text-xs">to</span>
                      <span className="font-mono">{b.end}</span>
                      <button
                        type="button"
                        onClick={() => handleRemove(key, i)}
                        disabled={delBusy}
                        className="ml-auto p-1 rounded-full text-zinc-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                        aria-label="Remove block"
                        data-testid={`schedule-remove-${key}-${i}`}
                      >
                        {delBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="flex items-center gap-2">
              <Input
                type="time"
                value={draft[key].start}
                onChange={(e) => setDraftFor(key, { start: e.target.value })}
                className="bg-[#09090B] border-zinc-700 text-white w-28"
                data-testid={`schedule-start-${key}`}
              />
              <span className="text-zinc-500 text-xs">to</span>
              <Input
                type="time"
                value={draft[key].end}
                onChange={(e) => setDraftFor(key, { end: e.target.value })}
                className="bg-[#09090B] border-zinc-700 text-white w-28"
                data-testid={`schedule-end-${key}`}
              />
              <Button
                type="button"
                size="sm"
                onClick={() => handleAdd(key)}
                disabled={addBusy}
                className="btn-primary ml-auto"
                data-testid={`schedule-add-${key}`}
              >
                {addBusy ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Add block
                  </>
                )}
              </Button>
            </div>
          </div>
        );
      })}
      <p className="text-[11px] text-zinc-500 leading-relaxed pt-1">
        Clients can only book scheduled sessions within these windows. Add multiple blocks per day
        for split shifts (e.g. 9:00–12:00 and 14:00–17:00). Slots are shown as 1-hour bookings.
      </p>
    </div>
  );
};

export default WeeklyScheduleEditor;
